import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Typography,
} from "@material-ui/core";
import { useStyles } from "./style";
import useOneParkEmailChange from "./useOneParkEmailChange";
import { isValidEmail } from "../../../MobilePayment/Receipts/receiptValidation";

/**
 * Operator-initiated ONEpark email-change dialog. Opened by clicking the (read-only)
 * email field for a ONEpark-connected access holder. It collects a New Email, runs the
 * single chained validate->initiate call (server-side), and shows validation errors
 * inline just like the ONEpark rewards portal. On success it closes with no success
 * toast; the caller surfaces the pending-verification indicator (Req 9.3).
 */
const ChangeOneParkEmailDialog = ({
    open,
    onClose,
    currentEmail,
    onChangeOneParkEmail,
    onInitiated,
}) => {
    const classes = useStyles();
    const [newEmail, setNewEmail] = useState("");

    const { isSubmitting, errorMessage, submit, clearError } =
        useOneParkEmailChange(onChangeOneParkEmail);

    const normalizedCurrent = (currentEmail || "").trim().toLowerCase();
    const normalizedNew = useMemo(() => newEmail.trim().toLowerCase(), [newEmail]);

    const formatInvalid = newEmail.length > 0 && !isValidEmail(normalizedNew);
    const sameAsCurrent =
        normalizedNew.length > 0 && normalizedNew === normalizedCurrent;
    const canSubmit =
        isValidEmail(normalizedNew) && !sameAsCurrent && !isSubmitting;

    // Reset local state whenever the dialog is (re)opened so a prior attempt does not leak in.
    useEffect(() => {
        if (open) {
            setNewEmail("");
            clearError();
        }
    }, [open, clearError]);

    const handleClose = () => {
        if (isSubmitting) return; // do not close mid-request
        onClose();
    };

    const handleSubmit = async () => {
        if (!canSubmit) return;
        const ok = await submit(normalizedNew);
        if (ok) {
            onInitiated(normalizedNew); // caller shows pending indicator, no success toast
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="xs"
            className="change-onepark-email-dialog"
            PaperProps={{ className: "change-onepark-email-dialog-paper" }}
            BackdropProps={{ classes: { root: classes.backdrop } }}
        >
            <DialogTitle className="change-onepark-email-dialog-title">Change ONEpark Email</DialogTitle>
            <DialogContent>
                <DialogContentText component="div">
                    <Typography variant="caption" className={classes.currentEmailLabel}>
                        Current Email
                    </Typography>
                    <Typography variant="body2" className={classes.currentEmailValue}>
                        {currentEmail || "—"}
                    </Typography>
                </DialogContentText>

                <TextField
                    className={clsx("change-onepark-email-new-email", classes.dialogField)}
                    autoFocus
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="New Email"
                    type="email"
                    value={newEmail}
                    onChange={(e) => {
                        if (errorMessage) clearError();
                        setNewEmail(e.target.value);
                    }}
                    error={formatInvalid || sameAsCurrent}
                    helperText={
                        formatInvalid
                            ? "Enter a valid email address."
                            : sameAsCurrent
                            ? "Enter an email address different from the current email."
                            : " "
                    }
                    inputProps={{ "aria-label": "New Email" }}
                />

                <DialogContentText component="div">
                    <Typography variant="caption" className={classes.currentEmailLabel}>
                        A verification link will be sent to the new email. The email will not
                        change until verification completes.
                    </Typography>
                </DialogContentText>

                {errorMessage && (
                    <Typography
                        role="alert"
                        variant="body2"
                        className={clsx("change-onepark-email-error", classes.dialogError)}
                    >
                        {errorMessage}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    className="change-onepark-email-cancel"
                    variant="outlined"
                    onClick={handleClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                <Button
                    className="change-onepark-email-submit"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    startIcon={
                        isSubmitting ? (
                            <CircularProgress size={14} color="inherit" />
                        ) : undefined
                    }
                >
                    {isSubmitting ? "Sending..." : "Send Verification"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

ChangeOneParkEmailDialog.defaultProps = {
    open: false,
    currentEmail: "",
    onClose: () => {},
    onChangeOneParkEmail: undefined,
    onInitiated: () => {},
};

ChangeOneParkEmailDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    currentEmail: PropTypes.string,
    onChangeOneParkEmail: PropTypes.func,
    onInitiated: PropTypes.func,
};

export default ChangeOneParkEmailDialog;
